import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Loader2, CheckCircle2, Circle, Camera, FileText, ClipboardList,
  Upload, Trash2, Sparkles, AlertTriangle, ShieldCheck, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (url) => `${BACKEND}${url}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`;

const SECTION_LABELS = {
  duties: 'Duties & Responsibilities',
  event_briefing: 'Event Briefing',
  performance_expectations: 'Performance Expectations',
  reporting_requirements: 'Reporting Requirements',
  required_photos: 'Required Photos / Evidence',
  dress_code: 'Dress Code & Appearance',
  parking_logistics: 'Parking & Logistics',
};
const SECTION_ORDER = ['event_briefing', 'duties', 'performance_expectations', 'dress_code', 'parking_logistics', 'required_photos', 'reporting_requirements'];

const CAT_META = {
  task: { Icon: ClipboardList, tone: 'text-slate-500' },
  photo: { Icon: Camera, tone: 'text-fuchsia-600' },
  report: { Icon: FileText, tone: 'text-indigo-600' },
};

const asText = (v) => (Array.isArray(v) ? v.join('\n') : (v || ''));

const UploadThumb = ({ u, onDelete }) => {
  const isImg = (u.content_type || '').startsWith('image/');
  return (
    <div className="relative group" data-testid={`evidence-${u.id}`}>
      {isImg ? (
        <a href={fileUrl(u.url)} target="_blank" rel="noreferrer">
          <img src={fileUrl(u.url)} alt={u.filename}
               className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
        </a>
      ) : (
        <a href={fileUrl(u.url) + '&download=true'} target="_blank" rel="noreferrer"
           className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 grid place-items-center text-[10px] text-slate-600 px-1 text-center">
          <FileText className="w-4 h-4 mb-0.5" />
          {(u.filename || 'file').slice(-10)}
        </a>
      )}
      {onDelete && (
        <button onClick={() => onDelete(u.id)}
                className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full w-5 h-5 grid place-items-center shadow opacity-90"
                data-testid={`delete-evidence-${u.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export const TalentTaskCard = ({ bookingId, onLoaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showBriefing, setShowBriefing] = useState(true);
  const [report, setReport] = useState('');
  const [evie, setEvie] = useState(null);
  const [evieBusy, setEvieBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tasks/bookings/${bookingId}`);
      setData(res.data);
      setReport(res.data?.report?.body || '');
      if (res.data?.briefing_acknowledged) setShowBriefing(false);
      if (onLoaded) onLoaded(!!res.data?.has_approved_requirements);
    } catch {
      setData(null);
      if (onLoaded) onLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [bookingId, onLoaded]);

  useEffect(() => { load(); }, [load]);

  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/bookings/${bookingId}/ack`);
      toast.success('Briefing acknowledged — your tasks are unlocked.');
      setShowBriefing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not acknowledge briefing.');
    } finally { setBusy(false); }
  };

  const toggle = async (item) => {
    if (!data?.briefing_acknowledged) { toast.info('Acknowledge the briefing first.'); return; }
    const next = !item.done;
    setData((p) => ({ ...p, items: p.items.map((i) => i.id === item.id ? { ...i, done: next } : i) }));
    try {
      await api.post(`/tasks/bookings/${bookingId}/items/${item.id}/toggle`, { done: next });
      await load();
    } catch (e) {
      toast.error('Could not update task.');
      await load();
    }
  };

  const pickFile = (key) => fileInputs.current[key]?.click();

  const doUpload = async (file, kind, taskItemId, key) => {
    if (!file) return;
    setUploadingId(key);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    if (taskItemId) fd.append('task_item_id', taskItemId);
    try {
      await api.post(`/tasks/bookings/${bookingId}/uploads`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Evidence uploaded.');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed — please try again.');
    } finally { setUploadingId(null); }
  };

  const removeUpload = async (uploadId) => {
    try {
      await api.delete(`/tasks/uploads/${uploadId}?booking_id=${bookingId}`);
      await load();
    } catch { toast.error('Could not remove evidence.'); }
  };

  const submitReport = async () => {
    if (report.trim().length < 2) { toast.error('Write a short report first.'); return; }
    setBusy(true);
    try {
      await api.post(`/tasks/bookings/${bookingId}/report`, { body: report.trim() });
      toast.success('End-of-shift report submitted.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit report.');
    } finally { setBusy(false); }
  };

  const askEvie = async () => {
    setEvieBusy(true);
    try {
      const res = await api.get(`/tasks/bookings/${bookingId}/evie`);
      setEvie(res.data?.message || "You're all caught up on your approved duties.");
    } catch {
      setEvie('Evie is unavailable right now — please check your task list.');
    } finally { setEvieBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="talent-tasks-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your checklist…
      </div>
    );
  }
  if (!data || !data.has_approved_requirements) return null;

  const items = data.items || [];
  const done = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  const briefing = data.briefing || {};
  const briefingEntries = SECTION_ORDER
    .filter((k) => asText(briefing[k]).trim())
    .map((k) => ({ key: k, label: SECTION_LABELS[k], value: asText(briefing[k]) }));
  const checklistArr = Array.isArray(briefing.task_checklist) ? briefing.task_checklist : [];

  return (
    <div className="space-y-3" data-testid="talent-task-card">
      {/* Pre-shift briefing */}
      <div className="bg-white rounded-xl border border-indigo-200 overflow-hidden" data-testid="briefing-card">
        <button onClick={() => setShowBriefing((s) => !s)}
                className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50"
                data-testid="briefing-toggle">
          <span className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <ShieldCheck className="w-4 h-4" /> Pre-Shift Briefing
            {data.briefing_acknowledged
              ? <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Acknowledged</span>
              : <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Action required</span>}
          </span>
          {showBriefing ? <ChevronUp className="w-4 h-4 text-indigo-700" /> : <ChevronDown className="w-4 h-4 text-indigo-700" />}
        </button>
        {showBriefing && (
          <div className="px-4 py-3 space-y-3" data-testid="briefing-body">
            {briefingEntries.map((e) => (
              <div key={e.key} data-testid={`briefing-section-${e.key}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{e.label}</div>
                <p className="text-sm text-slate-800 whitespace-pre-line">{e.value}</p>
              </div>
            ))}
            {checklistArr.length > 0 && (
              <div data-testid="briefing-section-checklist">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Task Checklist</div>
                <ul className="text-sm text-slate-800 list-disc pl-4">
                  {checklistArr.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
            {!data.briefing_acknowledged ? (
              <Button onClick={ack} disabled={busy}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mt-1"
                      data-testid="ack-briefing-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                I have read & understood — start my shift
              </Button>
            ) : (
              <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledged on {new Date(data.acknowledged_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Progress */}
      {items.length > 0 && (
        <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-3 flex items-center gap-3" data-testid="task-progress">
          <div className="relative w-12 h-12">
            <svg viewBox="0 0 36 36" className="w-12 h-12">
              <circle cx="18" cy="18" r="16" stroke="#e2e8f0" strokeWidth="3" fill="none" />
              <circle cx="18" cy="18" r="16" stroke="#0891b2" strokeWidth="3" strokeLinecap="round" fill="none"
                      strokeDasharray="100.5" strokeDashoffset={100.5 - pct} transform="rotate(-90 18 18)" />
            </svg>
            <span className="absolute inset-0 grid place-items-center text-cyan-700 font-bold text-xs">{pct}%</span>
          </div>
          <div>
            <div className="font-semibold text-sm text-slate-900">{done} of {items.length} tasks done</div>
            <p className="text-xs text-slate-600">Complete each approved duty and attach required evidence.</p>
          </div>
        </div>
      )}

      {/* Checklist */}
      <ol className="space-y-2" data-testid="task-items">
        {items.map((item) => {
          const meta = CAT_META[item.category] || CAT_META.task;
          const locked = !data.briefing_acknowledged;
          return (
            <li key={item.id}
                className={`bg-white rounded-xl border p-3 ${item.missing_upload ? 'border-amber-300' : 'border-slate-200'}`}
                data-testid={`task-item-${item.id}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggle(item)} disabled={locked}
                        className={`mt-0.5 w-7 h-7 rounded-full grid place-items-center shrink-0 ${
                          item.done ? 'bg-emerald-500 text-white shadow' : 'bg-slate-100 border border-slate-300 text-slate-500'
                        } ${locked ? 'opacity-40' : ''}`}
                        data-testid={`task-toggle-${item.id}`}>
                  {item.done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3 h-3" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <meta.Icon className={`w-3.5 h-3.5 ${meta.tone}`} />
                    <span className={`text-sm ${item.done ? 'line-through text-slate-500' : 'font-medium text-slate-800'}`}>
                      {item.label}
                    </span>
                  </div>
                  {item.requires_upload && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        {(item.uploads || []).map((u) => (
                          <UploadThumb key={u.id} u={u} onDelete={removeUpload} />
                        ))}
                        <input type="file" className="hidden"
                               accept="image/*,application/pdf,.csv,.xlsx,.xls"
                               ref={(el) => (fileInputs.current[item.id] = el)}
                               onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; doUpload(f, item.category === 'photo' ? 'photo' : 'lead_sheet', item.id, item.id); }}
                               data-testid={`task-file-input-${item.id}`} />
                        <button onClick={() => pickFile(item.id)} disabled={locked || uploadingId === item.id}
                                className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-400 hover:border-cyan-400 hover:text-cyan-500"
                                data-testid={`task-upload-btn-${item.id}`}>
                          {uploadingId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        </button>
                      </div>
                      {item.missing_upload && (
                        <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-1" data-testid={`missing-upload-${item.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Evidence required for this task
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* End-of-shift report */}
      {data.report_required && (
        <div className="bg-white rounded-xl border border-indigo-200 p-3" data-testid="report-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-slate-900">End-of-Shift Report</span>
            {data.report && <span className="text-[11px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Submitted</span>}
          </div>
          <Textarea value={report} onChange={(e) => setReport(e.target.value)} rows={4}
                    placeholder="Summarize totals, highlights and any issues from your shift."
                    className="bg-white text-sm" data-testid="report-input" />
          <Button onClick={submitReport} disabled={busy}
                  className="mt-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="report-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {data.report ? 'Update report' : 'Submit report'}
          </Button>
          {data.report?.submitted_at && (
            <p className="text-[11px] text-slate-500 mt-1">Last submitted {new Date(data.report.submitted_at).toLocaleString()}</p>
          )}
        </div>
      )}

      {/* Evie task coach */}
      <div className="bg-gradient-to-br from-fuchsia-50 to-indigo-50 rounded-xl border border-fuchsia-200 p-3" data-testid="evie-coach-card">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm font-semibold text-fuchsia-900">
            <Sparkles className="w-4 h-4" /> Evie Task Coach
          </span>
          <Button onClick={askEvie} disabled={evieBusy} size="sm" variant="outline"
                  className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-100" data-testid="evie-ask-btn">
            {evieBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Remind me'}
          </Button>
        </div>
        {evie && <p className="text-sm text-slate-800 whitespace-pre-line mt-2" data-testid="evie-message">{evie}</p>}
        {!evie && <p className="text-xs text-slate-500 mt-1">Evie only reminds you of your approved duties — never adds new ones.</p>}
      </div>
    </div>
  );
};

export default TalentTaskCard;
